import React from 'react';
import { TrendingUp } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  changePillClass,
  dashboardAccentChipClass,
  type DashboardAccent,
} from '../themeHelpers';

export interface StatCardProps {
  title: string;
  value: string | number;
  change?: string;
  /** Style for the change line: success (green), warning (amber), neutral (muted) */
  changeVariant?: 'success' | 'warning' | 'neutral';
  /** Optional muted line under the title (e.g. My work / Team scope). */
  subtitle?: string;
  icon: React.ReactNode;
  /** Theme semantic accent for the icon chip (settings-style). */
  accent?: DashboardAccent;
  /** @deprecated Prefer `accent` — ignored when accent is set. */
  gradient?: string;
  isLoading?: boolean;
  /** When true, no outer card (used inside DraggableWidget to avoid card-in-card) */
  embedded?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changeVariant = 'success',
  subtitle,
  icon,
  accent = 'info',
  isLoading,
  embedded,
}) => {
  const { colors } = useTheme();
  const wrapperClass = embedded
    ? 'min-w-0'
    : cn(
        colors.surface,
        'rounded-2xl shadow-md shadow-black/5 hover:shadow-lg hover:shadow-black/5 transition-shadow duration-200 p-5 border',
        colors.border,
      );

  const showTrendArrow = changeVariant === 'success';

  return (
    <div className={wrapperClass}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className={cn('truncate text-sm font-medium', colors.textSecondary)}>{title}</p>
          {subtitle ? (
            <p className={cn('mt-0.5 truncate text-[11px]', colors.textMuted)}>{subtitle}</p>
          ) : null}
          {isLoading ? (
            <div className={cn('mt-2 h-8 w-20 animate-pulse rounded-lg', colors.surfaceHover)} />
          ) : (
            <p className={cn('mt-1 truncate text-2xl font-bold tracking-tight', colors.text)}>{value}</p>
          )}
          {change && !isLoading && (
            <div className="mt-2">
              <span className={changePillClass(colors, changeVariant)}>
                {showTrendArrow && <TrendingUp className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                <span className="min-w-0 truncate">{change}</span>
              </span>
            </div>
          )}
        </div>
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            dashboardAccentChipClass(colors, accent),
          )}
        >
          <span className="flex h-5 w-5 items-center justify-center [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
        </span>
      </div>
    </div>
  );
};

export default StatCard;
